import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import type { Task, Project, WorkSlot } from '../lib/types';

// ============================================
// TYPES
// ============================================

interface User {
  id: string;
  full_name?: string | null;
  email: string;
  profile_image_url?: string | null;
}

interface TeamPlanningModeContextType {
  // Team Planning mode state
  isTeamPlanningMode: boolean;
  setTeamPlanningMode: (enabled: boolean) => void;

  // Sidebar expansion state
  isSidebarExpanded: boolean;
  setSidebarExpanded: (expanded: boolean) => void;

  // Mini calendar date navigation
  currentDate: Date;
  setCurrentDate: (date: Date) => void;
  onDateSelect: ((date: Date) => void) | null;
  setOnDateSelect: (handler: ((date: Date) => void) | null) => void;

  // Week range
  weekStart: Date;
  weekEnd: Date;
  setWeekRange: (start: Date, end: Date) => void;

  // Data for sidebar
  tasks: Task[];
  setTasks: (tasks: Task[]) => void;
  projects: Project[];
  setProjects: (projects: Project[]) => void;
  workSlots: WorkSlot[];
  setWorkSlots: (slots: WorkSlot[]) => void;
  users: User[];
  setUsers: (users: User[]) => void;

  // User selection
  selectedUserIds: string[];
  setSelectedUserIds: (ids: string[]) => void;
  onUserToggle: ((userId: string) => void) | null;
  setOnUserToggle: (handler: ((userId: string) => void) | null) => void;

  // Back handler
  onBack: (() => void) | null;
  setOnBack: (handler: (() => void) | null) => void;
}

// ============================================
// CONTEXT
// ============================================

const TeamPlanningModeContext = createContext<TeamPlanningModeContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface TeamPlanningModeProviderProps {
  children: ReactNode;
}

export function TeamPlanningModeProvider({ children }: TeamPlanningModeProviderProps) {
  const [isTeamPlanningMode, setIsTeamPlanningMode] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [onDateSelectState, setOnDateSelectState] = useState<((date: Date) => void) | null>(null);
  const [weekStart, setWeekStart] = useState<Date>(new Date());
  const [weekEnd, setWeekEnd] = useState<Date>(new Date());
  const [tasks, setTasks] = useState<Task[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [workSlots, setWorkSlots] = useState<WorkSlot[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [onUserToggleState, setOnUserToggleState] = useState<((userId: string) => void) | null>(null);
  const [onBackState, setOnBackState] = useState<(() => void) | null>(null);

  const setTeamPlanningMode = useCallback((enabled: boolean) => {
    setIsTeamPlanningMode(enabled);
    // Reset sidebar expansion when exiting team planning mode
    if (!enabled) {
      setIsSidebarExpanded(false);
    }
  }, []);

  const setSidebarExpanded = useCallback((expanded: boolean) => {
    setIsSidebarExpanded(expanded);
  }, []);

  const setWeekRange = useCallback((start: Date, end: Date) => {
    setWeekStart(start);
    setWeekEnd(end);
  }, []);

  const setOnDateSelect = useCallback((handler: ((date: Date) => void) | null) => {
    setOnDateSelectState(() => handler);
  }, []);

  const setOnUserToggle = useCallback((handler: ((userId: string) => void) | null) => {
    setOnUserToggleState(() => handler);
  }, []);

  const setOnBack = useCallback((handler: (() => void) | null) => {
    setOnBackState(() => handler);
  }, []);

  const value: TeamPlanningModeContextType = useMemo(() => ({
    isTeamPlanningMode,
    setTeamPlanningMode,
    isSidebarExpanded,
    setSidebarExpanded,
    currentDate,
    setCurrentDate,
    onDateSelect: onDateSelectState,
    setOnDateSelect,
    weekStart,
    weekEnd,
    setWeekRange,
    tasks,
    setTasks,
    projects,
    setProjects,
    workSlots,
    setWorkSlots,
    users,
    setUsers,
    selectedUserIds,
    setSelectedUserIds,
    onUserToggle: onUserToggleState,
    setOnUserToggle,
    onBack: onBackState,
    setOnBack,
  }), [
    isTeamPlanningMode,
    setTeamPlanningMode,
    isSidebarExpanded,
    setSidebarExpanded,
    currentDate,
    onDateSelectState,
    setOnDateSelect,
    weekStart,
    weekEnd,
    setWeekRange,
    tasks,
    projects,
    workSlots,
    users,
    selectedUserIds,
    onUserToggleState,
    setOnUserToggle,
    onBackState,
    setOnBack,
  ]);

  return (
    <TeamPlanningModeContext.Provider value={value}>
      {children}
    </TeamPlanningModeContext.Provider>
  );
}

// ============================================
// HOOKS
// ============================================

export function useTeamPlanningMode() {
  const context = useContext(TeamPlanningModeContext);
  if (context === undefined) {
    throw new Error('useTeamPlanningMode must be used within a TeamPlanningModeProvider');
  }
  return context;
}

/**
 * Safe version that returns null if no provider is present.
 */
export function useTeamPlanningModeSafe(): TeamPlanningModeContextType | null {
  const context = useContext(TeamPlanningModeContext);
  return context ?? null;
}
